import type { ScoreHistory } from '../index.js'
import type { ScoringRules } from '../../facilities/index.js'
import type { ShiftOutcome } from '../../partner/index.js'
import type { WorkerNotification } from '../../messages/index.js'
import type { WorkerTier } from '../../core/index.js'
import { OUTCOME_KINDS, groupOutcomesByMonth, type OutcomeGroup } from './outcomeGroups.js'

/**
 * A worker's record arranged one panel per month: the score that month
 * (the final of a closed month, the latest running score of the current
 * one) over the shifts that produced it, with a calendar of how each day
 * counted, and what the worker was told that month. Pure: built from the
 * score history, the outcomes and the notifications, and the facility's
 * scoring rules for what "on time" and "short notice" mean.
 */

/** The same defaults the API applies when a facility has not set its rules (`ScoringRulesSchema`). Used only until the config loads. */
export const DEFAULT_DAY_RULES: Pick<ScoringRules, 'graceMinutes' | 'shortNoticeHours'> = { graceMinutes: 5, shortNoticeHours: 4 }

/**
 * How a shift counted: for the worker, somewhat against (worked, but late or
 * left early), against (a call-out with notice), or heavily against (short
 * notice, or no show at all) - the engine's own distinctions, in four tones.
 */
export type DayStanding = 'for' | 'partly' | 'against' | 'severe'

const STANDING_RANK: Record<DayStanding, number> = { for: 0, partly: 1, against: 2, severe: 3 }

export interface DayCell {
  /** 1-based day of the month. */
  day: number
  date: string
  /** The shifts that day, one standing record per shift. */
  outcomes: readonly ShiftOutcome[]
  /** The worst of the day's shifts, or null for a day with none. */
  standing: DayStanding | null
}

/** A month laid out for a calendar: leading blanks so the first day sits under its weekday (Sunday first), then one cell per day. */
export interface MonthCalendarData {
  leadingBlanks: number
  days: readonly DayCell[]
}

export interface MonthPanelData extends MonthCalendarData {
  /** `YYYY-MM`. */
  month: string
  score: number | null
  tier: WorkerTier | null
  provisional: boolean
  /** Where the score came from: a closed month's final, the current month's running score, or no score at all. */
  scoreKind: 'final' | 'running' | 'none'
  isCurrent: boolean
  group: OutcomeGroup | null
  /** What the worker was told about this month, as the API lists them (newest first). */
  notifications: readonly WorkerNotification[]
}

const MINUTE_MS = 60 * 1000
const HOUR_MS = 60 * MINUTE_MS

/**
 * Where several outcomes name the same shift, the most recently recorded one
 * stands - as the engine counts them. Sorted oldest record first so the
 * latest lands last and wins the key; the input's order is otherwise kept.
 */
export function latestPerShift(outcomes: readonly ShiftOutcome[]): ShiftOutcome[] {
  const byRecord = [...outcomes].sort((a, b) => a.recordedAt.localeCompare(b.recordedAt))
  const standing = Object.fromEntries(byRecord.map((outcome) => [`${outcome.shiftDate}|${outcome.scheduledStart}`, outcome]))
  const kept = new Set(Object.values(standing).map((outcome) => outcome.eventId))
  return outcomes.filter((outcome) => kept.has(outcome.eventId))
}

export function standingOf(outcome: ShiftOutcome, rules: Pick<ScoringRules, 'graceMinutes' | 'shortNoticeHours'>): DayStanding {
  if (outcome.kind === 'no_show') {
    return 'severe'
  }
  if (outcome.kind === 'callout') {
    const shortNotice = outcome.reportedAt !== null && Date.parse(outcome.scheduledStart) - Date.parse(outcome.reportedAt) < rules.shortNoticeHours * HOUR_MS
    return shortNotice ? 'severe' : 'against'
  }
  const grace = rules.graceMinutes * MINUTE_MS
  const onTime = outcome.actualStart !== null && Date.parse(outcome.actualStart) <= Date.parse(outcome.scheduledStart) + grace
  const workedToEnd = outcome.actualEnd !== null && Date.parse(outcome.actualEnd) >= Date.parse(outcome.scheduledEnd) - grace
  return onTime && workedToEnd ? 'for' : 'partly'
}

function worstStanding(standings: readonly DayStanding[]): DayStanding | null {
  return standings.reduce<DayStanding | null>((worst, standing) => (worst === null || STANDING_RANK[standing] > STANDING_RANK[worst] ? standing : worst), null)
}

function parseMonth(month: string): { year: number; monthIndex: number } | null {
  const match = /^(\d{4})-(\d{2})$/.exec(month)
  return match ? { year: Number(match[1]), monthIndex: Number(match[2]) - 1 } : null
}

/** The month as a calendar, each day holding the shifts reported on it (one standing record per shift expected) and how the worst of them counted. */
export function buildMonthCalendar(month: string, outcomes: readonly ShiftOutcome[], rules: Pick<ScoringRules, 'graceMinutes' | 'shortNoticeHours'>): MonthCalendarData {
  const parsed = parseMonth(month)
  if (!parsed) {
    return { leadingBlanks: 0, days: [] }
  }
  const { year, monthIndex } = parsed
  const dayCount = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate()
  const days = Array.from({ length: dayCount }, (_, index) => {
    const day = index + 1
    const date = new Date(Date.UTC(year, monthIndex, day)).toISOString().slice(0, 10)
    const onDay = outcomes.filter((outcome) => outcome.shiftDate === date)
    return { day, date, outcomes: onDay, standing: worstStanding(onDay.map((outcome) => standingOf(outcome, rules))) }
  })
  return { leadingBlanks: new Date(Date.UTC(year, monthIndex, 1)).getUTCDay(), days }
}

/**
 * The month a message belongs to: the period of the score it was built
 * from when the engine sent it, otherwise the month it was created - an
 * imported message has no score behind it.
 */
export function notificationMonth(notification: WorkerNotification): string {
  return (notification.basedOn?.from ?? notification.createdAt).slice(0, 7)
}

/** The month the running score is for: the history's current period, or, until that loads, the month `now` is in (UTC). */
export function currentMonthOf(history: ScoreHistory | null, now: Date): string {
  return history ? history.period.from.slice(0, 7) : now.toISOString().slice(0, 7)
}

/** The months the history knows about: every final's month, and the current period's. */
function historyMonths(history: ScoreHistory | null): string[] {
  if (!history) {
    return []
  }
  return [history.period.from.slice(0, 7), ...history.finals.map((final) => final.from.slice(0, 7))]
}

function scoreFor(month: string, history: ScoreHistory | null): Pick<MonthPanelData, 'score' | 'tier' | 'provisional' | 'scoreKind' | 'isCurrent'> {
  const isCurrent = history?.period.from.slice(0, 7) === month
  if (history && isCurrent) {
    const latest = history.running.at(-1)
    return { score: latest?.score ?? null, tier: null, provisional: latest?.provisional ?? false, scoreKind: latest ? 'running' : 'none', isCurrent }
  }
  const final = history?.finals.find((candidate) => candidate.from.slice(0, 7) === month)
  if (final) {
    return { score: final.score, tier: final.tier, provisional: final.provisional, scoreKind: 'final', isCurrent }
  }
  return { score: null, tier: null, provisional: false, scoreKind: 'none', isCurrent }
}

/**
 * One panel per month that has a score, a shift or a message, newest
 * first. A month with shifts but no score (not yet scored, or too little
 * to score) still appears, as does a scored month whose shifts were
 * reported elsewhere.
 * Only the standing record of each shift is counted, in the header and
 * the calendar alike, so a corrected shift is one shift.
 */
export function buildMonthPanels(
  history: ScoreHistory | null,
  outcomes: readonly ShiftOutcome[],
  notifications: readonly WorkerNotification[],
  rules: Pick<ScoringRules, 'graceMinutes' | 'shortNoticeHours'>,
): MonthPanelData[] {
  const groups = groupOutcomesByMonth(latestPerShift(outcomes))
  const months = [...new Set([...historyMonths(history), ...groups.map((group) => group.month), ...notifications.map(notificationMonth)])].sort((a, b) => b.localeCompare(a))
  return months.map((month) => {
    const group = groups.find((candidate) => candidate.month === month) ?? null
    const told = notifications.filter((notification) => notificationMonth(notification) === month)
    return { month, ...scoreFor(month, history), group, notifications: told, ...buildMonthCalendar(month, group?.outcomes ?? [], rules) }
  })
}

export { OUTCOME_KINDS }
